/**
 * Google Sign-In button.
 *
 * Renders nothing unless VITE_GOOGLE_CLIENT_ID is configured. On success it
 * hands the Google ID token to the auth store; if the email already belongs to
 * a phone account, it shows a "connect from settings" hint instead of logging
 * in (the backend never auto-links).
 */

import { type CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getPostAuthLanding } from "@/features/auth/utils/post-auth-redirect";

/** Google sign-in renders only when a client id is configured. */
export function isGoogleEnabled(): boolean {
	return Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);
}

export function GoogleSignInButton() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const googleSignIn = useAuthStore((s) => s.googleSignIn);
	const needsOnboarding = useAuthStore((s) => s.needsOnboarding);
	const needsProfileSetup = useAuthStore((s) => s.needsProfileSetup);
	const getUserType = useAuthStore((s) => s.getUserType);
	const [linkEmail, setLinkEmail] = useState<string | null>(null);

	if (!isGoogleEnabled()) return null;

	const handleSuccess = async (response: CredentialResponse) => {
		if (!response.credential) {
			toast.error("Google sign-in failed. Please try again.");
			return;
		}
		try {
			const result = await googleSignIn(response.credential);
			if (result.linkRequired) {
				setLinkEmail(result.email ?? null);
				return;
			}
			toast.success(t("auth.loginSuccess"));
			const state = location.state as { redirectTo?: string } | null;
			const redirectTo = state?.redirectTo;
			if (redirectTo) {
				navigate(redirectTo);
			} else if (needsOnboarding()) {
				navigate("/onboarding");
			} else if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`);
			} else {
				navigate(getPostAuthLanding(getUserType()));
			}
		} catch (err) {
			// Surface the backend's actionable message (e.g. "Google email is not
			// verified", rate-limit copy) instead of a fixed generic string.
			const message =
				(err as { response?: { data?: { message?: string } } }).response?.data
					?.message ?? "Google sign-in failed. Please try again.";
			toast.error(message);
		}
	};

	return (
		<div className="mt-6">
			<div className="mb-6 flex items-center gap-3">
				<div className="h-px flex-1 bg-dashboard-border" />
				<span className="text-xs text-dashboard-muted">or</span>
				<div className="h-px flex-1 bg-dashboard-border" />
			</div>

			{linkEmail ? (
				<div
					className="rounded-md bg-dashboard-warm-450 p-3 text-sm text-dashboard-text"
					role="alert"
				>
					An account with {linkEmail} already exists. Sign in with your phone,
					then connect Google from settings.
				</div>
			) : (
				<div className="flex justify-center">
					<GoogleLogin
						onSuccess={handleSuccess}
						onError={() => {
							// onError fires for popup-blocked / COOP / misconfigured
							// authorized-origin — config issues worth a console breadcrumb.
							console.error("Google sign-in (GoogleLogin) onError");
							toast.error("Google sign-in failed. Please try again.");
						}}
						width="320"
					/>
				</div>
			)}
		</div>
	);
}
